import { useEffect, useRef, useState } from "react";
import { useGraphToken } from "../auth/useGraphToken";

export interface GraphMeProfile {
  displayName?: string;
  givenName?: string;
  surname?: string;
  jobTitle?: string;
  department?: string;
  officeLocation?: string;
  mail?: string;
  userPrincipalName?: string;
  mobilePhone?: string;
  businessPhones?: string[];
}

export interface ProfileState {
  profile: GraphMeProfile | null;
  photoUrl: string | null;
  loading: boolean;
  error: string | null;
}

const GRAPH_BASE = "https://graph.microsoft.com/v1.0";

/**
 * Loads `/me` + photo from Graph once, caches the photo blob URL across
 * the lifetime of the component. The photo endpoint returns 404 for users
 * without a profile picture — that is a normal case, not an error.
 */
export function useProfile(opts?: { skipPhoto?: boolean }): ProfileState {
  const getToken = useGraphToken();
  const skipPhoto = opts?.skipPhoto ?? false;
  const [state, setState] = useState<ProfileState>({
    profile: null,
    photoUrl: null,
    loading: true,
    error: null,
  });

  const photoBlobUrlRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const headers = { Authorization: `Bearer ${token}` };

        const meRes = await fetch(`${GRAPH_BASE}/me`, { headers });
        if (!meRes.ok) {
          throw new Error(`Graph /me failed (${meRes.status})`);
        }
        const profile = (await meRes.json()) as GraphMeProfile;
        if (cancelled) return;

        let photoUrl: string | null = null;
        if (!skipPhoto) {
          try {
            const photoRes = await fetch(`${GRAPH_BASE}/me/photo/$value`, {
              headers,
            });
            if (photoRes.ok) {
              const blob = await photoRes.blob();
              if (!cancelled) {
                const url = URL.createObjectURL(blob);
                photoBlobUrlRef.current = url;
                photoUrl = url;
              }
            }
          } catch {
            /* photo is optional */
          }
        }

        if (!cancelled) {
          setState({ profile, photoUrl, loading: false, error: null });
        }
      } catch (e) {
        if (!cancelled) {
          setState((s) => ({
            ...s,
            loading: false,
            error: e instanceof Error ? e.message : String(e),
          }));
        }
      }
    })();

    return () => {
      cancelled = true;
      if (photoBlobUrlRef.current) {
        URL.revokeObjectURL(photoBlobUrlRef.current);
        photoBlobUrlRef.current = null;
      }
    };
  }, [getToken, skipPhoto]);

  return state;
}
